/** @jsx jsx */
/**
 * @author        kyle.agronick@zumper.com (Kyle Agronick)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Notification
 */

import { Box } from '@chakra-ui/react'
import { css, jsx, keyframes } from '@emotion/react'
import { ClearButton } from '@zcode-ui/controls'
import { useMountAndUnmount } from '@zcode-ui/hook'
import type { ImgIconProps } from '@zcode-ui/icons'
import { cn } from '@zcode-ui/shadz-ui'
import React, { useCallback, useContext, useRef, useState } from 'react'
import ReactDOM, { flushSync } from 'react-dom'

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-40px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

const cssNotification = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  margin: 3px auto;
  box-shadow: 0px 16px 22px rgba(0, 0, 0, 0.08);
  border-radius: 24px;
  max-width: 496px;
  animation: ${fadeIn} 0.5s ease;
  pointer-events: visible;
  @media (max-width: 768px) {
    max-width: 316px;
    align-items: center;
  }
`

const cssNotificationBodyText = css`
  align-items: flex-start;
  flex-direction: column;
  gap: 10px;
  padding: 16px 24px;
`

const cssPositioner = css`
  pointer-events: none;
  position: fixed;

  top: 40px;
  z-index: 2000;

  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;

  width: fit-content;
  height: fit-content;

  @media (max-width: 768px) {
    top: 45px;
  }
`

const cssLabelContainer = css`
  justify-content: flex-start;
  white-space: wrap;
  height: initial;
  padding: 0;
`

const cssLabel = css`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const cssLabelBodyText = css`
  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const cssNotificationLabel = css`
  font-size: 16px;
  font-weight: 700;
  text-align: start;
  line-height: 22px;

  @media (max-width: 768px) {
    line-height: 20px;
  }
`

const textEllipsis = css`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const cssActionButton = css`
  text-decoration: underline;
  min-width: initial;
  font-size: 16px;
  max-height: 24px;
  margin-left: 24px;

  @media (max-width: 768px) {
    max-height: 20px;
  }
`

const cssFadeOut = css`
  animation: ${fadeOut} 0.5s ease;
`

interface Props {
  children: React.ReactNode
}

let messageId = 0

interface Notification {
  id: number
  display: NotificationDisplayProps
  exiting: boolean
  remove: () => void
  duration?: number
  timeout?: NodeJS.Timeout
}

export interface NotificationDisplayAction {
  label: string
  onClick: () => void
}
export interface NotificationDisplayProps {
  message: string
  bodyText?: string
  action?: NotificationDisplayAction
  icon?: React.FC<ImgIconProps>
  onHide?: () => void
  shouldTruncate?: boolean
  type?: 'default' | 'error'
}
/* istanbul ignore next */
export const AddNotificationContext = React.createContext<
  (display: NotificationDisplayProps, delay?: number) => () => void
>(() => () => null)

const createNotification = ({ display, id, exiting, remove }: Notification) => {
  const { icon: IconComponent, type } = display
  const classes = [cssNotification]
  const showBodyText = !!display.bodyText
  let ref = null

  if (showBodyText) {
    classes.push(cssNotificationBodyText)
  }

  if (exiting) {
    classes.push(cssFadeOut)
    ref = (elm: HTMLDivElement) => {
      if (elm) {
        elm.addEventListener('animationend', remove)
      }
    }
  }

  return (
    <div
      className={type === 'error' ? 'bg-pink-650' : 'bg-blue-900'}
      css={classes}
      data-testid="notification"
      key={id}
      ref={ref}
    >
      <div className="flex justify-between w-full">
        <ClearButton css={cssLabelContainer} onClick={remove}>
          {IconComponent && (
            <Box<'div'> height={5} mr={2}>
              <IconComponent />
            </Box>
          )}
          <div
            css={[
              cssLabel,
              cssNotificationLabel,
              display.shouldTruncate && textEllipsis,
              showBodyText && cssLabelBodyText,
            ]}
          >
            {display.message}
          </div>
        </ClearButton>
        {display?.action && (
          // eslint-disable-next-line react/jsx-handler-names
          <ClearButton
            css={[cssLabel, cssActionButton, showBodyText && cssLabelBodyText]}
            onClick={display.action.onClick}
          >
            {display.action.label}
          </ClearButton>
        )}
      </div>
      <div className="text-xs text-gray-0 leading-5">{display.bodyText}</div>
    </div>
  )
}

export const NotificationProvider = ({ children }: Props) => {
  const forceRender = useState(0)[1]
  const notificationRef = useRef<readonly Notification[]>([])
  const [isMounted, setIsMounted] = useState(false)

  const setNotifications = useCallback(
    (notifications: readonly Notification[]) => {
      notificationRef.current = notifications
      flushSync(() => {
        forceRender(Math.random)
      })
    },
    [notificationRef, forceRender]
  )

  const addNotification = useCallback(
    (display: NotificationDisplayProps, delay = 3000) => {
      const id = messageId++
      const remove = () => {
        setNotifications(notificationRef.current.filter(({ id: curId }) => curId !== id))
        display?.onHide?.()
      }

      const notification: Notification = {
        id,
        display,
        exiting: false,
        remove,
      }
      const hideNotification = () => {
        notification.exiting = true
        setNotifications([...notificationRef.current])
      }
      if (delay) {
        notification.timeout = setTimeout(hideNotification, delay)
      }
      setNotifications([...notificationRef.current, notification])
      return () => {
        if (notification.timeout) {
          clearTimeout(notification.timeout)
        }
        hideNotification()
      }
    },
    [setNotifications, notificationRef]
  )

  useMountAndUnmount({
    onMount: () => {
      setIsMounted(true)
    },
    onUnmount: () => {
      for (const notification of notificationRef.current) {
        if (notification.timeout) {
          clearTimeout(notification.timeout)
        }
      }
    },
  })

  return (
    <AddNotificationContext.Provider value={addNotification}>
      {children}
      {isMounted &&
        ReactDOM.createPortal(
          <div css={cssPositioner}>{notificationRef.current.map(createNotification)}</div>,
          document.body
        )}
    </AddNotificationContext.Provider>
  )
}

export const useAddNotification = () => {
  return useContext(AddNotificationContext)
}
